import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '../primitives/Button.js';
import { Modal } from '../primitives/Modal.js';
import { Text } from '../primitives/Text.js';

export interface ConfirmDeleteOptions {
  title: string;
  /** Plain string or rich markup (e.g. bold refs / supplier). */
  message: ReactNode;
  confirmLabel?: string;
  /** Confirm button tone — default danger (destructive). Use primary for irreversible send/commit. */
  confirmVariant?: 'danger' | 'primary';
  /** Label while onConfirm is in flight — default « Suppression… ». */
  pendingLabel?: string;
  onConfirm: () => void | Promise<void>;
}

type Setter = (opts: ConfirmDeleteOptions | null) => void;
let _setConfirm: Setter | null = null;

/** Mount once near app root — wires `openConfirmDelete` to a modal. */
export function ConfirmDeleteHost() {
  const [state, setState] = useState<ConfirmDeleteOptions | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    _setConfirm = (next) => {
      setPending(false);
      setError(null);
      setState(next);
    };
    return () => {
      _setConfirm = null;
    };
  }, []);
  const close = () => {
    if (pending) return;
    setState(null);
  };
  const confirm = async () => {
    if (!state || pending) return;
    setPending(true);
    setError(null);
    try {
      await state.onConfirm();
      setState(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'La suppression a échoué.';
      setError(message);
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      opened={!!state}
      onClose={close}
      title={state?.title ?? ''}
      footer={
        state ? (
          <>
            <Button variant="ghost" onClick={close} disabled={pending}>
              Annuler
            </Button>
            <Button
              variant={state.confirmVariant ?? 'danger'}
              onClick={confirm}
              loading={pending}
              aria-label={pending ? (state.pendingLabel ?? 'Suppression…') : undefined}
            >
              {state.confirmLabel ?? 'Supprimer'}
            </Button>
          </>
        ) : null
      }
    >
      {state ? (
        <>
          {typeof state.message === 'string' ? (
            <Text size="sm">{state.message}</Text>
          ) : (
            state.message
          )}
          {error ? (
            <Text size="sm" c="error" style={{ marginTop: 'var(--wp-space-8)' }}>
              {error}
            </Text>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

export function openConfirmDelete({
  title,
  message,
  confirmLabel = 'Supprimer',
  confirmVariant,
  pendingLabel,
  onConfirm,
}: ConfirmDeleteOptions) {
  _setConfirm?.({ title, message, confirmLabel, confirmVariant, pendingLabel, onConfirm });
}
